import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Stack,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import {
  FileText,
  Edit,
  Download,
  Printer,
  X,
  Save,
  FileCheck2,
  Menu,
} from "lucide-react";
import StyledTooltip from "../UI/StyledTooltip";
import TextEditor from "../TextEditor";
import ConfirmationDialog from "../../dialogs/ConfirmationDialog";
import { getPetitionTitles } from "../../actions/petitionActions";
import { showInsufficientCreditsModal } from "../../actions/creditActions";
import { deductCredits } from "../../utils/HelperFunction/deductCredits";
import html2pdf from "html2pdf.js";
import { updateDraftingHistoryDocumentApi } from "../../services/api/draftingHistoryApi";

const PetitionPreview = ({
  toggleSidebar,
  generatedPetition,
  setGeneratedPetition,
  isGenerating,
  petitionToEdit,
  setPetitionToEdit,
  readOnly,
  setReadOnly,
  isEditing,
  setIsEditing,
  isCollapsed,
}) => {
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const dispatch = useDispatch();
  const credits = useSelector((state) => state.credit.credits);
  const userId = useSelector((state) => state.credit.userId);
  const selectedPetitionState = useSelector((state) => state.selectedPetition);
  const { selectedPetition } = selectedPetitionState;
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const [editedContent, setEditedContent] = useState("");
  const [isPetitionEdited, setIsPetitionEdited] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  const contentWithStyles = `<style>
  body {
    font-family: 'Roboto', Helvetica, Arial, sans-serif !important;
  }
</style> ${generatedPetition?.html || selectedPetition?.Document}`;

  const cleanQuillOutput = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    div.querySelectorAll(".ql-align-center, .ql-align-right").forEach((el) => {
      if (el.classList.contains("ql-align-center")) {
        el.style.textAlign = "center";
        el.classList.remove("ql-align-center");
      }
      if (el.classList.contains("ql-align-right")) {
        el.style.textAlign = "right";
        el.classList.remove("ql-align-right");
      }
    });
    return div.innerHTML;
  };

  const handleEditPetition = (content) => {
    setEditedContent(content);
    setIsPetitionEdited(true);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setReadOnly(false);
  };

  const handleDownload = () => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }
    try {
      const rawHtml =
        generatedPetition?.html ||
        selectedPetition?.Document ||
        petitionToEdit?.Document;

      const cleanedHtml = cleanQuillOutput(rawHtml);
      const title =
        selectedPetition?.Title ||
        generatedPetition?.metadata?.Title ||
        petitionToEdit?.Title ||
        "Petition";
      const fileName = `${title} ${dayjs().format("DD-MM-YYYY")}.pdf`;

      const container = document.createElement("div");
      container.innerHTML = cleanedHtml;
      document.body.appendChild(container);

      html2pdf()
        .set({
          margin: 10,
          filename: fileName,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2 },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .from(container)
        .save()
        .then(() => {
          // Remove container after download
          document.body.removeChild(container);

          // Deduct credits
          const deductionSuccess = deductCredits(
            {
              userId,
              creditsToDeduct: 1,
              event: "Download petition on as PDF",
            },
            dispatch
          );

          if (!deductionSuccess) {
            console.warn(
              "Failed to deduct credits. Ensure retry or user notification."
            );
          }
        });
    } catch (error) {
      toast.error("Failed to download PDF. Please try again.", {
        position: "top-right",
      });
    }
  };

  const handlePrint = () => {
    const printContent = cleanQuillOutput(
      generatedPetition?.html || selectedPetition?.Document
    );

    // Create a hidden if0ame
    const iframe = document.createElement("iframe");
    iframe.style.position = "absolute";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    // Load the print content into the iframe
    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
      <html>
        <head>
          <title>Print</title>
          <style>
            .watermark {
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
              pointer-events: none;
              z-index: 9999;
              font-family: 'Roboto', Helvetica, Arial, sans-serif !important;
              font-size: 130px;
              color: rgba(0, 0, 0, 0.1);
              transform: rotate(-45deg);
            }
          </style>
        </head>
        <body>
          ${printContent}
          <div class="watermark">www.lawgpt.pk</div>
        </body>
      </html>
    `);
    iframeDoc.close();

    // Trigger the print dialog
    iframe.contentWindow.focus();
    iframe.contentWindow.print();

    // Ensure the iframe is cleared and removed after printing
    iframe.contentWindow.onafterprint = () => {
      iframeDoc.body.innerHTML = ""; // Clear content
      document.body.removeChild(iframe); // Remove the iframe
    };
  };

  const handleSaveClick = () => {
    if (editedContent) {
      setOpenConfirmationDialog(true);
    } else {
      return;
    }
  };

  const handleConfirmSave = () => {
    setOpenConfirmationDialog(false);
    handleSaveChanges();
  };
  const handleSaveChanges = async () => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }

    // Use toast.promise to manage toast notifications
    await toast.promise(
      updateDraftingHistoryDocumentApi("petition", {
        _id: selectedPetition?._id || petitionToEdit?._id,
        user_id: userInfo._id,
        Document: editedContent,
      }).then(async (response) => {
        if (response.status !== 200) {
          throw new Error("Failed to save changes");
        }
        const updatedPetition = response.data.data;
        if (generatedPetition?.html) {
          setGeneratedPetition((prev) => ({
            ...prev,
            html: updatedPetition.Document,
          }));
        } else {
          dispatch({
            type: "SET_SELECTED_PETITION",
            payload: { ...selectedPetition, ...updatedPetition },
          });
        }
        dispatch(getPetitionTitles('petition', userInfo._id));
        setEditedContent("");
        setIsEditing(false);
        setReadOnly(true);
        // Deduct credits
        const deductionSuccess = deductCredits(
          {
            userId,
            creditsToDeduct: 1,
            event: "Edit generated petition on petition drafting page",
          },
          dispatch
        );

        if (!deductionSuccess) {
          console.warn(
            "Failed to deduct credits. Ensure retry or user notification."
          );
        }
      }),
      {
        pending: { render: "Saving changes...", position: "top-right" },
        success: {
          render: "Changes saved successfully! 🎉",
          position: "top-right",
        },
        error: {
          render: "Failed to save changes. Please try again. 🚨",
          position: "top-right",
        },
      }
    );
  };

  const handleCancel = () => {
    setIsEditing(false);
    setReadOnly(true);
    setPetitionToEdit({});
  };

  useEffect(() => {
    if (Object.keys(petitionToEdit).length > 0) {
      setIsEditing(true);
      setReadOnly(false);
    }
    return () => {
      setIsEditing(false);
      // setReadOnly(true);
    };
  }, [petitionToEdit]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        ...(isMobileView
          ? { width: "100%" }
          : {
              flexBasis: isCollapsed ? "65%" : "54%",
              flexGrow: 1,
              transition: "flex-basis 0.3s ease",
            }),
        height: "100%",
        boxSizing: "border-box",
        margin: "12px",
        backgroundColor: "#f5f5f5",
        borderRadius: "12px",
        border: "1px solid #F1F5F9",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
        overflow: "hidden",
        fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          height: "100%",
          flexGrow: 1,
          bgcolor: "#fff",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 24px",
            borderBottom: "1px solid #F1F5F9",
            backgroundColor: "#FFFFFF",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <IconButton
              size="small"
              sx={{ 
                display: { xs: "flex", lg: "none" },
                color: "#64748B",
                "&:hover": {
                  backgroundColor: "#F1F5F9",
                }
              }}
              onClick={toggleSidebar}
            >
              <Menu size={20} />
            </IconButton>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                backgroundColor: "#F0FDF4",
                borderRadius: "12px",
                color: "#16A34A",
              }}
            >
              <FileCheck2 size={20} />
            </Box>
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#12243C",
                fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
              }}
            >
              Generated Petition
            </Typography>
          </Box>
                      {(generatedPetition?.html ||
              selectedPetition?.Document ||
              Object.keys(petitionToEdit).length > 0) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {isEditing
                ? [
                    {
                      title: "Save Changes",
                      icon: Save,
                      color: "#10B981",
                      onClick: handleSaveClick,
                      disabled: !isPetitionEdited,
                    },
                    {
                      title: "Cancel",
                      icon: X,
                      color: "#EF4444",
                      onClick: handleCancel,
                    },
                  ].map(({ title, icon: Icon, color, onClick, disabled }) => (
                    <StyledTooltip key={title} title={title}>
                      <IconButton
                        onClick={onClick}
                        disabled={disabled}
                        sx={{
                          width: "36px",
                          height: "36px",
                          backgroundColor: disabled ? "#F1F5F9" : "#F8FAFC",
                          color: disabled ? "#94A3B8" : color,
                          borderRadius: "8px",
                          "&:hover": {
                            backgroundColor: disabled ? "#F1F5F9" : "#F1F5F9",
                          },
                          "&:disabled": {
                            backgroundColor: "#F1F5F9",
                            color: "#94A3B8",
                          },
                        }}
                      >
                        <Icon size={16} />
                      </IconButton>
                    </StyledTooltip>
                  ))
                : [
                    {
                      title: "Edit",
                      icon: Edit,
                      color: "#3B82F6",
                      onClick: handleEdit,
                    },
                    {
                      title: "Download",
                      icon: Download,
                      color: "#F59E0B",
                      onClick: handleDownload,
                    },
                    {
                      title: "Print",
                      icon: Printer,
                      color: "#EF4444",
                      onClick: handlePrint,
                    },
                  ].map(({ title, icon: Icon, color, onClick, disabled }) => (
                    <StyledTooltip key={title} title={title}>
                      <IconButton 
                        onClick={onClick} 
                        disabled={disabled}
                        sx={{
                          width: "36px",
                          height: "36px",
                          backgroundColor: "#F8FAFC",
                          color: color,
                          borderRadius: "8px",
                          "&:hover": {
                            backgroundColor: "#F1F5F9",
                          },
                        }}
                      >
                        <Icon size={16} />
                      </IconButton>
                    </StyledTooltip>
                  ))}
            </Box>
          )}
        </Box>
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            padding: "24px",
            backgroundColor: "#FFFFFF",
            "::-webkit-scrollbar": {
              width: "6px",
            },
            "::-webkit-scrollbar-track": {
              background: "#F8FAFC",
            },
            "::-webkit-scrollbar-thumb": {
              background: "#CBD5E1",
              borderRadius: "3px",
              "&:hover": {
                background: "#94A3B8",
              },
            },
          }}
        >
          {isEditing ? (
            <Box
              sx={{ display: "flex", flexDirection: "column", height: "100%" }}
            >
              <TextEditor
                initialContent={
                  generatedPetition?.html ||
                  selectedPetition?.Document ||
                  petitionToEdit.Document
                }
                handleEdit={handleEditPetition}
              />
            </Box>
          ) : generatedPetition?.html || selectedPetition?.Document ? (
            <Box
              sx={{ display: "flex", flexDirection: "column", height: "100%" }}
            >
              <iframe
                title="Generated Petition Preview"
                srcDoc={contentWithStyles}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
                padding: "40px 24px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "80px",
                  height: "80px",
                  backgroundColor: "#F8FAFC",
                  borderRadius: "16px",
                  marginBottom: "24px",
                }}
              >
                <FileText size={32} color="#64748B" />
              </Box>
              <Typography 
                sx={{ 
                  fontSize: "16px",
                  fontWeight: "500",
                  color: "#475569",
                  textAlign: "center",
                  marginBottom: "8px",
                  fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                {isGenerating ? "Generating Petition..." : "No Petition Generated"}
              </Typography>
              <Typography 
                sx={{ 
                  fontSize: "14px",
                  color: "#94A3B8",
                  textAlign: "center",
                  fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                {isGenerating
                  ? "Please wait while we generate your petition..."
                  : "Preview of the draft will be shown here..."}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <ConfirmationDialog
        open={openConfirmationDialog}
        onClose={() => setOpenConfirmationDialog(false)}
        onConfirm={handleConfirmSave}
        title={"Confirm Save"}
        warningMessage={"Saving this document will replace the existing one."}
        confirmationPromptMessage={"Do you want to proceed?"}
        buttonText={isMobileView ? "Confirm" : "Confirm Save"}
      />
    </Box>
  );
};

export default PetitionPreview;
